import React from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import _ from 'lodash'
import moment from 'moment-timezone';
import {injectIntl} from 'react-intl';

import Flatpickr from 'react-flatpickr';
import {German} from 'flatpickr/dist/l10n/de';
import {english} from 'flatpickr/dist/l10n/default';
import InputWidget from './InputWidget';
import Form from 'react-bootstrap/Form';

const PICKER_TIME_ZONE = 'Europe/Berlin';
const PICKER_DATE_FORMAT = 'Y-m-dTH:i:S'; // Zeitzonen agnostischer 'ISO 8601 like' datetime String
const MOMENT_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

const FLATPICKR_ALT_FORMATS = {
    de: 'd.m.Y',
    // Wenn man die englische Sprachvariante als die internationale Variante versteht,
    // dann macht ISO 8601 am meisten Sinn für die Darstellung von Daten.
    en: 'Y-m-d'
};

const FLATPICKR_DATETIME_ALT_FORMATS = {
    de: 'd.m.Y H:i',
    en: 'Y-m-d H:i'
};

const FLATPICKR_LOCALES = {
    de: German,
    en: english
};

class DateWidget extends InputWidget {

    handleDateSelected = (selectedDates, dateString) => {
        // Bei Änderungen das Feld explizit als "touched" kennzeichnen, die Validierung
        // aber unterdrücken, da diese ohnehin durch setFieldValue() getriggert wird.
        this.props.formikProps.setFieldTouched(this.getFieldName(), true, false);
        this.props.formikProps.setFieldValue(this.getFieldName(), dateString);
    };

    /**
     * Holt die Datumsauswahl anhand der widgetId dieser Komponente aus der
     * vorherigen PaperSubmission
     *
     * @return {string}
     */
    getSelectedDate() {
        if (!this.props.submission) {
            return '';
        }

        const previousSelection = _.find(this.props.submission.formData, (formElement) => {
            return formElement.id === this.props.widget.id;
        });

        if (!previousSelection) {
            return '';
        }

        return (new Date(previousSelection.value)).toLocaleDateString();
    }

    render() {
        const flatpickrOptions = {
            wrap: true,
            altFormat: this.props.widget.hasTime ? FLATPICKR_DATETIME_ALT_FORMATS[this.props.intl.locale] : FLATPICKR_ALT_FORMATS[this.props.intl.locale],
            dateFormat: PICKER_DATE_FORMAT,
            altInput: true,
            altInputClass: this.isInvalid() ? 'is-invalid' : '',
            locale: FLATPICKR_LOCALES[this.props.intl.locale],
            time_24hr: true,
            enableTime: this.props.widget.hasTime || false,
            allowInput: true
        };
        // Auswahl beschränken falls nur bestimmte Daten ausgewählt werden dürfen
        // "enable: undefined" darf nicht gesetzt werden, nur setzen, wenn vorhanden
        if (this.props.widget.allowedDates && this.props.widget.allowedDates.length) {
            if (this.props.widget.hasTime) {
                flatpickrOptions['enable'] = this.props.widget.allowedDates.map(
                    d => {
                        return {
                            from: d + ' ' + moment().startOf('day').format('HH:mm:ss'),
                            to: d + ' ' + moment().endOf('day').format('HH:mm:ss')
                        }
                    }
                );
            } else {
                flatpickrOptions['enable'] = this.props.widget.allowedDates;
            }
        }
        // nur setzen, wenn vorhanden
        if (this.getValue()) {
            flatpickrOptions['defaultDate'] = moment.tz(this.getValue(), PICKER_TIME_ZONE).format(MOMENT_DATE_FORMAT);
        }

        return (
            <>
                {/* Da es wohl nicht möglich ist den Flatpickr zu disablen wird ein anderes Element gerendert */}
                {
                    this.props.disabled &&
                    <div style={{marginBottom: '20px'}}>
                        <label>{this.renderLabelText()}</label>
                        <Form.Control type="text" value={this.getSelectedDate()} readOnly/>
                    </div>
                }

                {/*
                     Da die yup Validierung asynchron erfolgt erzwingen wir durch Kombination von value und valid das neu
                     rendern des Pickers, da sonst Anzeige und Wert divergieren können, bspw. Wert ausgewählt aber Feld als
                     invalid markiert, weil vorher das Feld noch leer war.
                 */}
                {
                    !this.props.disabled &&
                    <Flatpickr key={this.getValue() + '/' + this.isInvalid()}
                               className="form-group"
                               name={this.getFieldName()}
                               options={flatpickrOptions}
                               data-testid={this.props.widget.id}
                               onChange={this.handleDateSelected}>
                        <label>{this.renderLabelText()}</label>
                        <div className="input-group">
                            <div className="input-group-prepend">
                                <button className="btn btn-outline-primary" type="button" data-toggle>
                                    <i className="fa fa-calendar" aria-hidden="true"/>
                                </button>
                            </div>
                            <input type="text" className="form-control" data-input/>
                            <div className="input-group-append">
                                <button className="btn btn-outline-primary" type="button" data-clear>
                                    <i className="fa fa-times" aria-hidden="true"/>
                                </button>
                            </div>
                        </div>
                        {this.isInvalid() ? <small className="form-text text-danger">{this.getError()}</small> : null}
                        {this.getCustomRequiredError()}
                    </Flatpickr>
                }
            </>
        );
    }
}

DateWidget.propTypes = {
    widget: PropTypes.object,
    intl: PropTypes.object
};

const IntlDateWidget = injectIntl(DateWidget);

export function type() {
    return 'date';
}

export function initialValue(widget) {
    return '';
}

/**
 * @param widget
 * @param allowPartialSubmissions
 * @returns {StringSchema<string | undefined | null, object>}
 */
export function schema(widget, allowPartialSubmissions) {
    // Während das Frontend immer nur ISO 8601 artige strings ohne Zeitzonen Informationen sendet, liefert die API
    // die Datumswerte in verschiedenen Formaten zurück, siehe hierzu die Testfälle in DataWidget.test.js.
    // Das liegt wohl daran, dass das Feature der optionalen Zeitangabe für dieses Widget erst später hinzugekommen ist
    // und die vorhandenen Daten niemals bereinigt worden sind und als response einfach 1:1 die Werte aus der Datenbank
    // zurückgeliefert werden. Die relevante Stelle in der API is DateFormWidget::transformValueForPersistence()
    let schema = yup.string().matches(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}([-+]\d{2}:\d{2}|\.\d{3}Z)?)?$/).nullable();

    // Es wird darauf verzichtet das ausgewählte Datum gegen die auswählbaren zu verifizieren,
    // da der Datepicker selber dafür sorgt, dass nur die relevanten Daten ausgewählt werden
    // können.

    if (!allowPartialSubmissions && widget.required) {
        schema = schema.required();
    }

    return schema;
}

export function component() {
    return IntlDateWidget;
}
